/**
 * Admin Route Component
 *
 * Wraps routes that require admin privileges.
 * SECURITY NOTE: This is UI-only protection.
 * Backend ALWAYS verifies admin status on every request.
 */
import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface AdminRouteProps {
  children: ReactNode;
}

/**
 * Loading spinner component
 */
const LoadingSpinner = () => (
  <div className="min-h-screen bg-gray-100 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-metrica-green mx-auto"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

/**
 * Admin route wrapper
 * - Shows loading spinner while checking auth
 * - Redirects to home if not admin
 * - Renders children if admin
 *
 * IMPORTANT: This is UI-only protection!
 * The backend @require_admin decorator provides the real security.
 */
export const AdminRoute = ({ children }: AdminRouteProps) => {
  const { isAnyAdmin, isLoading, isAuthenticated } = useAuth();

  // Show loading state while checking authentication
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Redirect to signin if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  // Redirect to home if not admin (super_admin or admin)
  if (!isAnyAdmin) {
    return <Navigate to="/" replace />;
  }

  // User is admin, render children
  return <>{children}</>;
};
